import { getLastCompletedMonthRange, getLastCompletedQuarterRange } from "utils";

import {
  ChartType,
  CorrelationMatrixSettings,
  CurrencyType,
  DateRangeValueType,
  DealFormAnswerValue,
  TaskDueDates,
  TaskPriority,
  TaskStatus
} from "../store/types";

export * from "./benchmark";
export * from "./covanents";
export * from "./dashboard-news";
export * from "./datarooms";
export * from "./deal";
export * from "./deal-form";
export * from "./debt";
export * from "./documents";
export * from "./empty-card";
export * from "./freemium";
export * from "./http";
export * from "./markup";
export * from "./measure-data";
export * from "./query-params";
export * from "./reports";
export * from "./routes";
export * from "./tagger";

export enum ErrorMessage {
  EmptyQuestion = "Please enter a question",
  EmptyQuestionAnswer = "Empty questions will be removed automatically",
  InvalidNumberAnswer = "Please enter numbers",
  MonitoringDataValidationFailure = "One or more of your inputs are incorrect, please review",
  NoMessage = "No message received"
}

export enum RoleName {
  Admin = "admin",
  Analyst = "team-member",
  Guest = "guest",
  Viewer = "viewer",
  TeamViewer = "team-viewer",
  Investor = "investor"
}

export enum FileType {
  JPG = "jpg",
  PNG = "png",
  CSV = "csv",
  XLX = "xlx",
  XLS = "xls",
  XLSX = "xlsx",
  PDF = "pdf",
  DOC = "doc",
  DOCX = "docx",
  TXT = "txt",
  PPT = "ppt",
  PPTX = "pptx",
  JPEG = "jpeg",
  FILE = "file"
}

export enum EntityType {
  SECTION = "section",
  DOCUMENT = "document",
  FOLDER = "folder",
  FORM = "form",
  EMAIL = "email",
  NOTE = "note",
  FILE = "file"
}

export const currencySign: Record<CurrencyType, string> = {
  USD: "$",
  EUR: "€",
  // NIS: "₪",
  ILS: "₪",
  // BTC: "₿",
  JPY: "¥",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  DKK: "Kr.",
  NZD: "NZ$",
  // CNY: "¥",
  CHF: "₣",
  PLN: "zł"
};

export const currenciesArray: CurrencyType[] = Object.keys(currencySign) as CurrencyType[];

export const defaultAnswer: DealFormAnswerValue = {
  value: ""
};

export const taskDueDatesLabels = [
  "Due before today",
  "Due today",
  "Due tomorrow",
  "Due this week",
  "Due next week",
  "Due next 14 days"
];

export const taskDueDates: { label: string; value: TaskDueDates }[] = [
  {
    label: "Due before today",
    value: "before_today"
  },
  {
    label: "Due today",
    value: "today"
  },
  {
    label: "Due tomorrow",
    value: "tomorrow"
  },
  {
    label: "Due this week",
    value: "this_week"
  },
  {
    label: "Due next week",
    value: "next_week"
  },
  {
    label: "Due next 14 days",
    value: "next_14_days"
  }
];

// Task statuses
export const taskStatuses: Record<TaskStatus, string> = {
  open: "Open",
  "in-progress": "In Progress",
  done: "Done"
};

export const taskStatusOptions: { value: TaskStatus; label: string }[] = Object.entries(taskStatuses).map(
  ([value, label]) => ({
    value: value as TaskStatus,
    label
  })
);

export enum DocumentDataField {
  SelectedInvestor = "selectedInvestor",
  SelectedRound = "selectedRound",
  SelectedDate = "selectedDate",
  SelectedCurrency = "selectedCurrency"
}

export enum DocumentFileDataField {
  DocumentType = "document_type",
  InvestorId = "investor_id"
}

export const capitalize = (s: string): string => s[0].toUpperCase() + s.slice(1);

// Task Priorities
export const taskPriorities: TaskPriority[] = ["low", "medium", "high"];

export const taskPrioritiesObj: Record<TaskPriority, string> = taskPriorities.reduce(
  (acc, item) => ({ ...acc, [item]: capitalize(item) }),
  {} as Record<TaskPriority, string>
);

export const taskPrioritiesOptions: { label: string; value: TaskPriority }[] = taskPriorities.map((item) => ({
  label: capitalize(item),
  value: item
}));

export const dateRangeOptions: {
  key: DateRangeValueType;
  value: DateRangeValueType;
  text: string;
  category: "relative" | "absolute";
  info: string;
}[] = [
  // Relative Range Options
  {
    key: "lastCalendarMonth",
    value: "lastCalendarMonth",
    text: "Last Calendar Month",
    category: "relative",
    info: `The most recent full month completed (e.g., ${getLastCompletedMonthRange()}).`
  },
  {
    key: "lastCalendarQuarter",
    value: "lastCalendarQuarter",
    text: "Last Calendar Quarter",
    category: "relative",
    info: `The most recent full calendar quarter completed (e.g., ${getLastCompletedQuarterRange()}).`
  },
  {
    key: "lastYear",
    value: "lastYear",
    text: "Last Calendar Year",
    category: "relative",
    info: "The last full calendar year (Jan 1–Dec 31)."
  },
  {
    key: "ytd",
    value: "ytd",
    text: "YTD",
    category: "relative",
    info: "From Jan 1 this year through today, inclusive."
  },
  {
    key: "last3Years",
    value: "last3Years",
    text: "Last 3 Years",
    category: "relative",
    info: "Trailing 36 months up to and including today."
  },
  {
    key: "last5Years",
    value: "last5Years",
    text: "Last 5 Years",
    category: "relative",
    info: "Trailing 60 months up to and including today."
  },
  // Specific option for deal report
  // Should excluded on other places except deal report
  {
    key: "inceptionToLastNAV",
    value: "inceptionToLastNAV",
    text: "Inception to Last NAV",
    category: "relative",
    info: "From the entity’s inception (or earliest data) through the last NAV date."
  },
  {
    key: "fromInception",
    value: "fromInception",
    text: "ITD",
    category: "relative",
    info: "From the entity’s inception (or earliest data) through today."
  },

  // Absolute Range Options
  {
    key: "dateRange",
    value: "dateRange",
    text: "Date Range",
    category: "absolute",
    info: "From start of day on the first date to end of day on the second date (inclusive)."
  },
  {
    key: "selectQuarter",
    value: "selectQuarter",
    text: "Quarter Range",
    category: "absolute",
    info: "From the start of the first quarter to the end of the last quarter you select."
  },
  {
    key: "selectYear",
    value: "selectYear",
    text: "Select Year",
    category: "absolute",
    info: "Jan 1–Dec 31 of the year you select."
  }
];

export const fundFilesTypes = [
  "Capital call",
  "Capital distribution",
  "Financial statement",
  "Capital account",
  "Legal",
  "Tax",
  "Quarterly update",
  "Other",
  "Stock Purchase Agreement",
  "Investor Rights Agreement",
  "COI"
];

export const fileSources = ["Dataroom", "Email", "Direct upload"];

export const chartTypesCollection: Record<ChartType, string> = {
  pie: "Pie",
  bar: "Bar",
  column: "Column",
  barStacked: "Stacked Bar",
  columnStacked: "Stacked Column",
  waterfall: "Waterfall",
  top5: "Top 5",
  scatter: "X/Y Scatter",
  clusterBar: "Cluster Bar"
};

export const defaultCorrelationMatrixSettings: CorrelationMatrixSettings = {
  dateRange: { from: "2020-01-01", to: new Date().toISOString().slice(0, 10) },
  items: [{ index: "^SPX" }, { index: "^IXIC" }]
  // indexes: ["^SPX", "^IXIC"],
  // filters: {}
};

export const EXTRACTABLE_DOCUMENT_TYPES = ["Capital call", "Capital account", "Capital distribution"];
